/**
 * 건물 관리 수정 컴포넌트
 * 계약의 건물 운영 정보를 수정하는 컴포넌트입니다.
 * 건물 선택, 대표 건물 지정, 건물 목록 관리 등의 기능을 제공합니다.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BuildingModel } from "src/api/building/building-types";
import { postManagementBuildingsAsync } from "src/api/contract/contract-api";
import { ContractDetail, CtBuildingManage } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { Modal } from "src/api/public-types";
import { BaseButton } from "src/components";
import BaseSection from "src/components/layout/BaseSection";
import ProductBuildingListModal from "src/pages/product/components/ProductBuildingListModal";
import { useToast } from "src/recoil/toast/hook";
import BuildingManagementTable from "../detailComponent/BuildingManagementTable";

/**
 * 건물 관리 폼 데이터 타입
 */
type FormData = {
  buildingManageList: CtBuildingManage[]; // 건물 관리 목록
};

/**
 * 건물 관리 수정 컴포넌트의 props 인터페이스
 */
interface BasicProps {
  detailData: any; // 상세 데이터
  contract: ContractDetail; // 계약 상세 정보
  saveFlags: boolean; // 저장 플래그
  setEditable: any; // 편집 가능 상태 설정 함수
  setSaveFlags: any; // 저장 플래그 설정 함수
  setData: Function; // 데이터 업데이트 함수
  popupState: boolean; // 팝업 상태
  setPopupState: any; // 팝업 상태 설정 함수
  setCommonModals: any; // 공통 모달 설정 함수
  setEditConfrim: any; // 편집 확인 설정 함수
  managementBuildingData: any; // 건물 관리 데이터
}

/**
 * 건물 관리 수정 컴포넌트
 * @param detailData 상세 데이터
 * @param saveFlags 저장 플래그
 * @param setEditable 편집 가능 상태 설정 함수
 * @param setSaveFlags 저장 플래그 설정 함수
 * @param setData 데이터 업데이트 함수
 * @param contract 계약 상세 정보
 * @param setCommonModals 공통 모달 설정 함수
 * @param setPopupState 팝업 상태 설정 함수
 * @param setEditConfrim 편집 확인 설정 함수
 * @param managementBuildingData 건물 관리 데이터
 */
const BuildingManagementEdit = ({
  detailData,
  saveFlags,
  setEditable,
  setSaveFlags,
  setData,
  contract,
  setCommonModals,
  setPopupState,
  setEditConfrim,
  managementBuildingData,
}: BasicProps) => {
  const { openToast } = useToast();
  const { partnerId } = useParams();
  const [buildingModal, setBuildingModal] = useState<Modal>({ isOpen: false }); // 빌딩 팝업 모달
  const [selectedRow, setSelectedRow] = useState({}); // 선택된 행 정보
  const [selectedBuilding, setSelectedBuilding] = useState<BuildingModel | null>(null); // 모달에서 선택한 빌딩
  const [previewManagementBuildingData, setPreviewOriginManagementBuildingData] = useState(managementBuildingData); // 미리보기용 건물 관리 데이터
  const { executeAsync: postManagementBuildings } = useApiOperation(postManagementBuildingsAsync); // 건물 관리 데이터 저장 API

  /**
   * 저장 플래그가 변경될 때 건물 관리 데이터 저장
   * 삭제, 등록, 대표 건물 변경 등의 처리를 수행합니다.
   */
  useEffect(() => {
    if (saveFlags === true) {
      // 초기에 조회해온 값(managementBuildingData)과 미리보기(previewManagementBuildingData)비교
      // 1: managementBuildingData 에는 있는데 previewManagementBuildingData 에는 없는 경우 == 삭제처리
      // 2: managementBuildingData 에는 없는데 previewManagementBuildingData는 있는 경우 == 등록처리
      // 3: isPrimary가 변경된 경우 == 변경 정보 처리

      const tableViewList = previewManagementBuildingData.filter((building: CtBuildingManage) => !building.isDeleted);
      const originIds = managementBuildingData.map((item: CtBuildingManage) => item.buildingId);
      const tableViewIds = tableViewList.map((item: CtBuildingManage) => item.buildingId);

      const deletedManagementBuildings = managementBuildingData
        .filter((building: CtBuildingManage) => !tableViewIds.includes(building.buildingId))
        .map((item: CtBuildingManage) => {
          return { buildingId: item.buildingId, isDeleted: true };
        });

      const addedManagementBuildings = tableViewList
        .filter((building: CtBuildingManage) => !originIds.includes(building.buildingId))
        .map((item: CtBuildingManage) => {
          return { buildingId: item.buildingId, isPrimary: item.isPrimary ? true : false, isDeleted: false };
        });

      const changedPrimaryManagementBuildings = tableViewList
        .filter((tableViewBuilding: CtBuildingManage) => {
          const originItem = managementBuildingData.find((item: CtBuildingManage) => item.buildingId === tableViewBuilding.buildingId);
          return originItem && originItem.isPrimary !== tableViewBuilding.isPrimary;
        })
        .map((building: CtBuildingManage) => {
          return { buildingId: building.buildingId, isPrimary: building.isPrimary };
        });

      const payload = [...deletedManagementBuildings, ...addedManagementBuildings, ...changedPrimaryManagementBuildings];

      (async () => {
        try {
          await postManagementBuildings({
            contractManageId: contract.contractManageId!,
            buildingManageList: payload,
          });
          setEditable(false);
          setCommonModals({
            message: "저장 되었습니다.",
            title: "건물 운영 정보 변경",
          });
        } catch {
          setCommonModals({ isOpen: false });
        }
      })();
      setSaveFlags(false);
      setPopupState(true); // true가 되면 commonModal이 열림
      setEditConfrim(false); // '저장하시겠습니까?'를 출력하는 모달 닫기
    }
  }, [saveFlags]);

  /**
   * 컴포넌트 마운트 시 대표 건물 정보 설정
   */
  useEffect(() => {
    const findPrimaryManageBuilding = previewManagementBuildingData.find((building: CtBuildingManage) => building.isPrimary);
    setSelectedRow(findPrimaryManageBuilding);
  }, []);

  return (
    <div>
      <div className="flex-center">
        <BaseSection
          title={"건물"}
          children={<BaseButton title="선택" className="color-white" onClick={() => setBuildingModal({ isOpen: true })} />}
        />
      </div>
      <div>
        <BuildingManagementTable
          viewTable={previewManagementBuildingData.filter((building: any) => !building.isDeleted) || []}
          setViewTable={setPreviewOriginManagementBuildingData}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          onSelectedRowsChange={setSelectedRow}
        />

        {buildingModal.isOpen && (
          <ProductBuildingListModal
            setSelectedBuilding={setSelectedBuilding}
            partnerId={partnerId!}
            initialArray={previewManagementBuildingData}
            onClose={() => setBuildingModal({ isOpen: false })}
            onConfirm={() => {
              let changeToManagementBuildingData = {
                address: selectedBuilding?.address,
                buildingId: Number(selectedBuilding?.id),
                buildingName: selectedBuilding?.buildingName,
                isPrimary: false,
                isDeleted: selectedBuilding?.isDeleted,
              };
              setPreviewOriginManagementBuildingData([...previewManagementBuildingData, changeToManagementBuildingData]);
              setBuildingModal({ isOpen: false });
            }}
          />
        )}
      </div>
    </div>
  );
};
export default BuildingManagementEdit;
